import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FilterPopoverPill,
  Guard,
  Pill,
  ResultCount,
  type FilterPopoverPillOption,
  type PillVariant,
} from '@jywer/ui';
import { fetchAdminOrders, fetchOrdersSalesReps } from './api';
import type { AdminOrderRow, OrderStatusFilter, SalesRepRef } from './types';

type DatePresetKey = 'all' | 'this_month' | 'last_month' | 'q1' | 'custom';

const STATUS_OPTIONS: FilterPopoverPillOption<OrderStatusFilter>[] = [
  { value: 'active', label: 'Aktive' },
  { value: 'voided', label: 'Annullerede' },
];

const STATUS_VARIANT: Record<OrderStatusFilter, PillVariant> = {
  active: 'success',
  voided: 'muted',
};

function startOfMonth(d: Date): string {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
}
function endOfMonth(d: Date): string {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
}

function resolveDatePreset(preset: DatePresetKey): { from?: string; to?: string } {
  const now = new Date();
  if (preset === 'this_month') return { from: startOfMonth(now), to: endOfMonth(now) };
  if (preset === 'last_month') {
    const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    return { from: startOfMonth(last), to: endOfMonth(last) };
  }
  if (preset === 'q1') {
    return {
      from: `${now.getUTCFullYear()}-01-01`,
      to: `${now.getUTCFullYear()}-03-31`,
    };
  }
  return {};
}

const PRESET_LABELS: Record<DatePresetKey, string> = {
  all: 'Alle',
  this_month: 'Denne måned',
  last_month: 'Sidste måned',
  q1: 'Q1',
  custom: 'Brugerdef.',
};

function formatRegisteredDate(iso: string): string {
  const d = new Date(iso);
  // dd-MM-yyyy in da-DK; tabular-friendly.
  return d.toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatTotal(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString('da-DK', {
    style: 'currency',
    currency: 'DKK',
    maximumFractionDigits: 0,
  });
}

export function OrdersPage() {
  const [rows, setRows] = useState<AdminOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [reps, setReps] = useState<SalesRepRef[]>([]);
  const [salesRepIds, setSalesRepIds] = useState<string[]>([]);
  const [status, setStatus] = useState<OrderStatusFilter[]>(['active']);
  const [search, setSearch] = useState('');
  const [datePreset, setDatePreset] = useState<DatePresetKey>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const dateRange = useMemo(() => {
    if (datePreset === 'custom') {
      return { from: customFrom || undefined, to: customTo || undefined };
    }
    return resolveDatePreset(datePreset);
  }, [datePreset, customFrom, customTo]);

  const repOptions: FilterPopoverPillOption<string>[] = useMemo(
    () => reps.map((r) => ({ value: r.id, label: r.display_name })),
    [reps],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await fetchAdminOrders({
        sales_rep_ids: salesRepIds.length > 0 ? salesRepIds : undefined,
        search: search || undefined,
        status,
        date_from: dateRange.from,
        date_to: dateRange.to,
        page,
        pageSize,
      });
      setRows(data.rows);
      setTotal(data.total);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Kunne ikke hente ordrer');
    } finally {
      setLoading(false);
    }
  }, [salesRepIds, search, status, dateRange, page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void fetchOrdersSalesReps().then((r) => {
      if (!cancelled) setReps(r);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="mb-1 text-2xl font-semibold text-primary">Ordrer</h1>
          <p className="text-sm text-ink-2">
            Alle registrerede ordrer — filtrér efter sælger, kunde eller periode.
          </p>
        </div>
        <Guard viewId="edit:admin.orders">
          <Link
            to="/orders/new"
            className="rounded bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary/90"
          >
            + Ny ordre
          </Link>
        </Guard>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded border border-line-1 bg-paper-0 px-4 py-3">
        <FilterPopoverPill
          label="Sælger"
          countNoun={{ singular: 'sælger', plural: 'sælgere' }}
          options={repOptions}
          selected={salesRepIds}
          onChange={(next) => {
            setPage(1);
            setSalesRepIds(next);
          }}
        />
        <FilterPopoverPill
          label="Status"
          countNoun={{ singular: 'status', plural: 'statusser' }}
          options={STATUS_OPTIONS}
          selected={status}
          onChange={(next) => {
            setPage(1);
            setStatus(next);
          }}
        />
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          placeholder="Søg kunde, kontaktperson…"
          className="w-64 rounded border border-line-2 px-2.5 py-1.5 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <div className="flex flex-wrap items-center gap-1">
          {(Object.keys(PRESET_LABELS) as DatePresetKey[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setPage(1);
                setDatePreset(k);
              }}
              className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                datePreset === k
                  ? 'border-primary bg-primary text-white'
                  : 'border-line-2 bg-paper-0 text-ink-1 hover:border-primary'
              }`}
            >
              {PRESET_LABELS[k]}
            </button>
          ))}
        </div>
        {datePreset === 'custom' ? (
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded border border-line-2 px-2 py-1 text-sm"
            />
            <span className="text-xs text-ink-2">→</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded border border-line-2 px-2 py-1 text-sm"
            />
          </div>
        ) : null}
        <div className="ml-auto">
          <ResultCount shown={rows.length} total={total} singularNoun="ordre" pluralNoun="ordrer" />
        </div>
      </div>

      {err ? (
        <div className="rounded border border-status-err/40 bg-status-err/5 px-4 py-3 text-sm text-status-err">
          {err}
        </div>
      ) : null}

      {loading && rows.length === 0 ? (
        <div className="rounded border border-line-1 bg-paper-0 px-4 py-10 text-center text-ink-2">
          Henter ordrer…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded border border-line-1 bg-paper-0 px-4 py-10 text-center text-ink-2">
          Ingen ordrer matcher filtrene.
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-line-1 bg-paper-0">
          <table className="w-full text-sm">
            <thead className="border-b border-line-1 bg-paper-1 text-left text-xs uppercase tracking-wide text-ink-2">
              <tr>
                <th className="px-3 py-2 font-medium">Dato</th>
                <th className="px-3 py-2 font-medium">Kunde</th>
                <th className="px-3 py-2 font-medium">Sælger(e)</th>
                <th className="px-3 py-2 font-medium">Produkter</th>
                <th className="px-3 py-2 text-right font-medium">Ordreværdi</th>
                <th className="px-3 py-2 font-medium">Kontrakt</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Åbn</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const sellerLabel = r.extra_sales_rep_name
                  ? `${r.sales_rep_name ?? '—'} + ${r.extra_sales_rep_name} (${r.extra_sales_rep_share_pct ?? 0}%)`
                  : (r.sales_rep_name ?? '—');
                return (
                  <tr
                    key={r.id}
                    className="border-b border-line-1 last:border-b-0 hover:bg-paper-1"
                  >
                    <td className="px-3 py-2 text-ink-1 tabular-nums">
                      {formatRegisteredDate(r.registered_at)}
                    </td>
                    <td className="px-3 py-2 text-ink-0">{r.client_name}</td>
                    <td className="px-3 py-2 text-ink-2">{sellerLabel}</td>
                    <td className="px-3 py-2 text-ink-1">{r.product_summary}</td>
                    <td className="px-3 py-2 text-right text-ink-0 tabular-nums">
                      {formatTotal(r.total_value)}
                    </td>
                    <td className="px-3 py-2 text-ink-2">
                      {r.signed_contract_present ? 'Ja' : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <Pill variant={STATUS_VARIANT[r.is_voided ? 'voided' : 'active']}>
                        {r.is_voided ? 'Annulleret' : 'Aktiv'}
                      </Pill>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/orders/${r.id}/edit`}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Åbn ↗
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {lastPage > 1 ? (
        <div className="flex items-center justify-center gap-2 text-xs text-ink-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-line-2 px-2 py-1 disabled:opacity-40"
          >
            ← Forrige
          </button>
          <span>
            Side {page} / {lastPage}
          </span>
          <button
            type="button"
            disabled={page >= lastPage}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            className="rounded border border-line-2 px-2 py-1 disabled:opacity-40"
          >
            Næste →
          </button>
        </div>
      ) : null}
    </div>
  );
}
